

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  duration: number;
  delay: number;
}

interface ParticleEffectsProps {
  isActive: boolean;
  intensity?: 'low' | 'medium' | 'high';
  colors?: string[];
}

const DEFAULT_COLORS = ['#fbbf24', '#f59e0b', '#d97706', '#fcd34d'];
const PRIME_COLORS = ['#ec4899', '#d946ef', '#a855f7', '#f472b6'];
const LEGENDARY_COLORS = ['#fbbf24', '#f59e0b', '#fcd34d', '#fde68a'];

export function ParticleEffects({
  isActive,
  intensity = 'medium',
  colors = DEFAULT_COLORS,
}: ParticleEffectsProps) {
  const [particles, setParticles] = useState<Particle[]>([]);

  const particleCount = intensity === 'low' ? 15 : intensity === 'medium' ? 30 : 50;

  useEffect(() => {
    if (isActive) {
      const newParticles: Particle[] = [];
      for (let i = 0; i < particleCount; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 6 + 2,
          color: colors[Math.floor(Math.random() * colors.length)],
          duration: Math.random() * 2 + 1,
          delay: Math.random() * 0.5,
        });
      }
      setParticles(newParticles);
    } else {
      setParticles([]);
    }
  }, [isActive, particleCount, colors]);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-40">
      <AnimatePresence>
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute rounded-full"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: particle.size,
              height: particle.size,
              backgroundColor: particle.color,
              boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`,
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0, 1, 1, 0],
              scale: [0, 1, 1.5, 0],
              y: [0, -100 - Math.random() * 200],
              x: [0, (Math.random() - 0.5) * 100],
            }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{
              duration: particle.duration,
              delay: particle.delay,
              ease: 'easeOut',
            }}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

// Confetti effect for special reveals
export function ConfettiEffect({ isActive }: { isActive: boolean }) {
  const [confetti, setConfetti] = useState<Array<{
    id: number;
    x: number;
    rotation: number;
    color: string;
    size: number;
    duration: number;
  }>>([]);

  useEffect(() => {
    if (isActive) {
      const pieces = [];
      for (let i = 0; i < 60; i++) {
        pieces.push({
          id: i,
          x: Math.random() * 100,
          rotation: Math.random() * 360,
          color: ['#fbbf24', '#ec4899', '#a855f7', '#3b82f6', '#22c55e'][
            Math.floor(Math.random() * 5)
          ],
          size: Math.random() * 10 + 5,
          duration: Math.random() * 2 + 2,
        });
      }
      setConfetti(pieces);
    } else {
      setConfetti([]);
    }
  }, [isActive]);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-40">
      <AnimatePresence>
        {confetti.map((piece) => (
          <motion.div
            key={piece.id}
            className="absolute"
            style={{
              left: `${piece.x}%`,
              top: '-20px',
              width: piece.size,
              height: piece.size * 0.6,
              backgroundColor: piece.color,
              borderRadius: '2px',
            }}
            initial={{ opacity: 0, y: -20, rotate: 0 }}
            animate={{
              opacity: [0, 1, 1, 0],
              y: ['0vh', '100vh'],
              rotate: [0, piece.rotation, piece.rotation * 2, piece.rotation * 3],
              x: [0, (Math.random() - 0.5) * 100, (Math.random() - 0.5) * 150],
            }}
            exit={{ opacity: 0 }}
            transition={{
              duration: piece.duration,
              ease: 'linear',
            }}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

export { PRIME_COLORS, LEGENDARY_COLORS, DEFAULT_COLORS };
